// vistas/VistaTarjetasImagen.jsx
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Search, Loader2, CheckCircle, AlertCircle, Package, X as XIcon, Users, Plus, Minus, Percent, Trash2, Eye, EyeOff } from 'lucide-react';
import MultiSelectFilter from './MultiSelectFilter';
import VistaResumen from './PedidosResumen';
import { useQuery } from '../QueryContext';
import apiClient from '../api/client';
import imagengeneral from '../assets/imagengeneral.jpg';

const formatUSD = (num) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num || 0);

// ================================================================
// MAPEO DE PROVEEDORES
// ================================================================
const MAPEO_PROVEEDORES = {
  'BEYAZ KAGIT VE HIJYENIK ÜRÜNLER TEM. INS SAN. TIC A.S.': 'BEYAZ KAGIT',
  'BEYAZ KAGIT': 'BEYAZ KAGIT',
  'Canalcare, S.A.': 'Canalcare, S.A.',
  'CANALCARE': 'Canalcare, S.A.',
  'COLOMBIANA KIMBERLY COLPAPEL S.A.': 'KIMBERLY-CLARK',
  'KIMBERLY-CLARK PERÚ S.R.L.': 'KIMBERLY-CLARK',
  'KIMBERLY-CLARK': 'KIMBERLY-CLARK',
  'KIMBERLY CLARK': 'KIMBERLY-CLARK',
  'EDGEWELL PERSONAL CARE, LLC': 'EDGEWELL',
  'EDGEWELL': 'EDGEWELL',
  'ENERGIZER, LLC': 'ENERGIZER, LLC',
  'ENERGIZER': 'ENERGIZER, LLC',
  'PURISSIMA DE VENEZUELA I C.A.': 'PURISSIMA',
  'PURISSIMA': 'PURISSIMA',
  'UNILEVER ANDINA VENEZUELA, S.A.': 'UNILEVER INTERNATIONAL',
  'UNILEVER ASIA PRIVATE LIMITED': 'UNILEVER INTERNATIONAL',
  'UNILEVER': 'UNILEVER INTERNATIONAL',
  'UNILEVER INTERNATIONAL': 'UNILEVER INTERNATIONAL',
  'VENTEKS DIS TICARET LIMITED SIRKETI': 'VENTEKS',
  'VENTEKS': 'VENTEKS',
  '': 'SIN PROVEEDOR',
};

const normalizarProveedor = (nombreOriginal) => {
  if (!nombreOriginal || nombreOriginal.trim() === '') return 'SIN PROVEEDOR';
  const nombreLimpio = nombreOriginal.trim().toUpperCase();
  if (MAPEO_PROVEEDORES[nombreLimpio]) return MAPEO_PROVEEDORES[nombreLimpio];
  for (const [clave, valor] of Object.entries(MAPEO_PROVEEDORES)) {
    if (clave && (nombreLimpio.includes(clave) || clave.includes(nombreLimpio))) return valor;
  }
  return nombreOriginal.trim();
};

// ================================================================
// FUNCIÓN AUXILIAR PARA PRECIOS
// ================================================================
const getPrecioProducto = (product, field = 'usd') => {
  if (!product) return 0;
  const posiblesCampos = [
    field === 'usd' ? 'precio_usd' : 'precio_bs',
    field === 'usd' ? 'precioUsd' : 'precioBs',
    field === 'usd' ? 'precio_dolar' : 'precio_bolivar',
    field === 'usd' ? 'price_usd' : 'price_bs',
    field === 'usd' ? 'priceUSD' : 'priceBS',
    'precio',
    'price',
  ];
  for (const campo of posiblesCampos) {
    if (product[campo] !== undefined && product[campo] !== null) {
      const val = parseFloat(product[campo]);
      if (!isNaN(val) && val > 0) return val;
    }
  }
  if (product.precios && typeof product.precios === 'object') {
    const val = field === 'usd' ? product.precios.usd || product.precios.USD : product.precios.bs || product.precios.BS;
    if (val !== undefined && val !== null) {
      const parsed = parseFloat(val);
      if (!isNaN(parsed) && parsed > 0) return parsed;
    }
  }
  return 0;
};

// ================================================================
// COMPONENTE PRINCIPAL
// ================================================================
const VistaTarjetasImagen = ({ hook }) => {
  const { getProductosCliente } = useQuery();

  const {
    setSelectedClients,
    orderLines,
    handleAddProduct,
    clientesOptions,
    handleUpdateClientQuantity,
    handleUpdateClientDiscount,
    handleUpdateClientPacDiscount,
    handleRemoveProduct,
    successModal,
    closeSuccessModal,
  } = hook;

  // Estado local
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [selectedProveedores, setSelectedProveedores] = useState([]);
  const [selectedMarcas, setSelectedMarcas] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [mostrarSinPrecio, setMostrarSinPrecio] = useState(false);
  const [localAssignments, setLocalAssignments] = useState({});
  const [imagenAmpliada, setImagenAmpliada] = useState(null);
  const [imagenesCache, setImagenesCache] = useState({});
  const [allProductosCliente, setAllProductosCliente] = useState([]);
  const [loadingProductos, setLoadingProductos] = useState(false);

  // Cargar productos cuando cambia el cliente
  useEffect(() => {
    if (!clienteSeleccionado) {
      setAllProductosCliente([]);
      setLocalAssignments({});
      setImagenesCache({});
      return;
    }
    const fetchProductos = async () => {
      setLoadingProductos(true);
      try {
        const data = await getProductosCliente(clienteSeleccionado);
        if (data?.success) setAllProductosCliente(data.productos);
      } catch (err) {
        console.error('Error cargando productos:', err);
        setAllProductosCliente([]);
      } finally {
        setLoadingProductos(false);
      }
    };
    fetchProductos();
  }, [clienteSeleccionado, getProductosCliente]);

  const handleSelectCliente = (clientId) => {
    if (clienteSeleccionado === clientId) {
      setClienteSeleccionado(null);
      setSelectedClients([]);
      setLocalAssignments({});
      setImagenesCache({});
    } else {
      setClienteSeleccionado(clientId);
      setSelectedClients([clientId]);
    }
  };

  const filteredClients = useMemo(() => {
    if (!clientSearchTerm) return clientesOptions;
    const term = clientSearchTerm.toLowerCase();
    return clientesOptions.filter(c =>
      c.value.toLowerCase().includes(term) ||
      c.label.toLowerCase().includes(term) ||
      (c.nombre && c.nombre.toLowerCase().includes(term))
    );
  }, [clientesOptions, clientSearchTerm]);

  // Productos filtrados
  const productosFiltrados = useMemo(() => {
    let prods = allProductosCliente;
    if (selectedProveedores.length > 0) {
      prods = prods.filter(p => {
        const prov = normalizarProveedor(p.proveedor || p.codigo_vendedor || 'SIN PROVEEDOR');
        return selectedProveedores.includes(prov);
      });
    }
    if (selectedMarcas.length > 0) {
      prods = prods.filter(p => selectedMarcas.includes(p.marca));
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      prods = prods.filter(p =>
        (p.sku && p.sku.toLowerCase().includes(term)) ||
        (p.nombre && p.nombre.toLowerCase().includes(term))
      );
    }
    if (!mostrarSinPrecio) {
      prods = prods.filter(p => {
        const precioUSD = getPrecioProducto(p, 'usd');
        const precioBS = getPrecioProducto(p, 'bs');
        return !(precioUSD <= 0 && precioBS <= 0);
      });
    }
    return prods;
  }, [allProductosCliente, selectedProveedores, selectedMarcas, searchTerm, mostrarSinPrecio]);

  // Cargar imagen con fetch + token
  // ✅ CAMBIAR la función cargarImagen para usar apiClient (que ya tiene el token)

const cargarImagen = useCallback(async (sku, ref) => {
  const cacheKey = ref || sku;
  if (imagenesCache[cacheKey] !== undefined) return;
  
  try {
    const response = await apiClient.get(`/pedidosapp/producto_imagen/${ref || sku}`, {
      responseType: 'blob'
    });
    
    if (response.status === 200 && response.data) {
      const url = URL.createObjectURL(response.data);
      setImagenesCache(prev => ({ ...prev, [cacheKey]: url }));
    } else {
      setImagenesCache(prev => ({ ...prev, [cacheKey]: null }));
    }
  } catch (err) {
    setImagenesCache(prev => ({ ...prev, [cacheKey]: null }));
  }
}, [imagenesCache]);

  // Cargar imágenes de productos visibles
  useEffect(() => {
  if (!productosFiltrados.length) return;
  
  const skusPendientes = productosFiltrados
    .slice(0, 500)
    .filter(prod => imagenesCache[prod.ref || prod.sku] === undefined);
  
  skusPendientes.forEach(prod => cargarImagen(prod.sku, prod.ref));
}, [productosFiltrados, imagenesCache, cargarImagen]);

  const proveedoresOptions = useMemo(() => {
    const uniqueProvs = new Set();
    allProductosCliente.forEach(p => {
      uniqueProvs.add(normalizarProveedor(p.proveedor || p.codigo_vendedor || 'SIN PROVEEDOR'));
    });
    return [...uniqueProvs].sort();
  }, [allProductosCliente]);

  const marcasOptions = useMemo(() => {
    const uniqueMarcas = new Set();
    allProductosCliente.forEach(p => {
      const provAgrupado = normalizarProveedor(p.proveedor || p.codigo_vendedor || 'SIN PROVEEDOR');
      if (selectedProveedores.length === 0 || selectedProveedores.includes(provAgrupado)) {
        uniqueMarcas.add(p.marca);
      }
    });
    return [...uniqueMarcas].sort();
  }, [allProductosCliente, selectedProveedores]);

  const isInOrder = (sku) => orderLines.some(l => l.sku === sku);

  const getLocalAssignment = (sku) => {
    return localAssignments[sku] || { cantidad: 0, descuento: 0, descuento_pac: 0 };
  };

  const updateLocalAssignment = (sku, updates) => {
    setLocalAssignments(prev => ({
      ...prev,
      [sku]: { ...getLocalAssignment(sku), ...updates }
    }));
  };

  const handleAddProductLocal = (product) => {
    const local = getLocalAssignment(product.sku);
    const precioUSD = getPrecioProducto(product, 'usd');
    const precioBS = getPrecioProducto(product, 'bs');
    
    const productFormatted = {
      sku: product.sku,
      marca: product.marca,
      nombre: product.nombre,
      uni_venta: product.uni_venta || 'UND',
      tipo_imp: product.tipo_imp || 1,
      equivalencia: product.equivalencia || 1.0,
      stock_bl: product.stock_bl,
      clientPrecios: {
        [clienteSeleccionado]: {
          precio_bs: precioBS,
          precio_bs2: parseFloat(product.precio_bs2 || 0) || 0,
          precio_usd: precioUSD,
          codigo_vendedor: product.codigo_vendedor || '01',
          cos_pro_un: parseFloat(product.cos_pro_un || 0) || 0,
          ult_cos_un: parseFloat(product.ult_cos_un || 0) || 0,
          cos_pro_om: parseFloat(product.cos_pro_om || 0) || 0,
          ult_cos_om: parseFloat(product.ult_cos_om || 0) || 0
        }
      }
    };
    
    handleAddProduct(productFormatted);
    
    if (local.cantidad > 0) handleUpdateClientQuantity(product.sku, clienteSeleccionado, local.cantidad);
    if (local.descuento > 0) handleUpdateClientDiscount(product.sku, clienteSeleccionado, local.descuento);
    if (local.descuento_pac > 0) handleUpdateClientPacDiscount(product.sku, clienteSeleccionado, local.descuento_pac);
  };

  const updateLocalCantidad = (sku, valor) => {
    const cant = Math.max(0, parseInt(valor) || 0);
    updateLocalAssignment(sku, { cantidad: cant });
    if (isInOrder(sku)) handleUpdateClientQuantity(sku, clienteSeleccionado, cant);
  };

  const updateLocalDescuento = (sku, valor) => {
    const desc = Math.min(100, Math.max(0, parseFloat(valor) || 0));
    updateLocalAssignment(sku, { descuento: desc });
    if (isInOrder(sku)) handleUpdateClientDiscount(sku, clienteSeleccionado, desc);
  };

  const updateLocalDescuentoPAC = (sku, valor) => {
    const descPAC = Math.min(100, Math.max(0, parseFloat(valor) || 0));
    updateLocalAssignment(sku, { descuento_pac: descPAC });
    if (isInOrder(sku)) handleUpdateClientPacDiscount(sku, clienteSeleccionado, descPAC);
  };

  return (
    <div className="mass-order-body">
      {/* COLUMNA 1 - CLIENTE ÚNICO */}
      <div className="mass-col mass-col-clients">
        <div className="col-header">
          <Users size={18} />
          <h3>Cliente</h3>
          {clienteSeleccionado && <span className="badge">1</span>}
        </div>
        <div className="col-search">
          <Search size={14} />
          <input type="text" placeholder="Buscar cliente..." value={clientSearchTerm} onChange={(e) => setClientSearchTerm(e.target.value)} />
        </div>
        <div className="client-list">
          {filteredClients.map(client => (
            <div key={client.value} className={`client-item ${clienteSeleccionado === client.value ? 'selected' : ''}`} onClick={() => handleSelectCliente(client.value)}>
              <div className="client-info">
                <div className="client-code">{client.value}</div>
                <div className="client-name">{client.nombre}</div>
                {client.porc_desc > 0 && <span style={{ fontSize: '0.6rem', color: '#f87171' }}>Desc. {client.porc_desc}%</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* COLUMNA 2 - GRID DE TARJETAS */}
      <div className="mass-col mass-col-products">
        <div className="col-header">
          <Package size={18} />
          <h3>Productos</h3>
          <span className="badge">{productosFiltrados.length}</span>
        </div>

        {/* Filtros */}
        <div className="product-filters" style={{ display: 'flex', flexDirection: 'row', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <MultiSelectFilter label="Proveedor" options={proveedoresOptions.map(p => ({ value: p, label: p }))} selected={selectedProveedores} onChange={setSelectedProveedores} />
          <MultiSelectFilter label="Marca" options={marcasOptions.map(m => ({ value: m, label: m }))} selected={selectedMarcas} onChange={setSelectedMarcas} />
          <div className="col-search" style={{ flex: 1, minWidth: '200px' }}>
            <Search size={14} />
            <input type="text" placeholder="Buscar SKU o nombre..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
          <button
            onClick={() => setMostrarSinPrecio(prev => !prev)}
            className="new-order-btn"
            style={{
              padding: '4px 10px', fontSize: '0.6rem', minWidth: 'auto',
              background: !mostrarSinPrecio ? 'rgba(0,186,82,0.15)' : 'rgba(239,68,68,0.15)',
              border: !mostrarSinPrecio ? '1px solid rgba(0,186,82,0.4)' : '1px solid rgba(239,68,68,0.4)',
              color: !mostrarSinPrecio ? '#4ade80' : '#f87171',
              display: 'flex', alignItems: 'center', gap: '4px'
            }}
          >
            {!mostrarSinPrecio ? <EyeOff size={12} /> : <Eye size={12} />}
            {!mostrarSinPrecio ? 'Ocultando Sin Precio' : 'Mostrar Sin Precio'}
          </button>
        </div>

        {/* Contenido */}
        {loadingProductos ? (
          <div className="loading-state"><Loader2 size={30} className="spin-anim" /><p>Cargando...</p></div>
        ) : !clienteSeleccionado ? (
          <div className="empty-state"><AlertCircle size={40} /><p>Seleccione un cliente</p></div>
        ) : (
          <div className="product-grid" style={{ padding: '0.5rem' }}>
            {productosFiltrados.map(p => {
              const added = isInOrder(p.sku);
              const sinStock = p.stock_bl !== null && p.stock_bl <= 0;
              const precioUSD = getPrecioProducto(p, 'usd');
              const precioBS = getPrecioProducto(p, 'bs');
              const sinPrecio = precioUSD <= 0 && precioBS <= 0;
              const imagenUrl = imagenesCache[p.ref || p.sku] || imagengeneral;
              const localAssign = getLocalAssignment(p.sku);
              const cantidad = localAssign.cantidad;
              const descPropuesta = localAssign.descuento;
              const descPAC = localAssign.descuento_pac;
              const subtotal = cantidad * precioUSD;

              return (
                <div key={p.sku} className={`product-card ${added ? 'added' : ''}`} style={{ opacity: sinPrecio ? 0.5 : 1, cursor: added ? 'default' : 'pointer' }}>
                  {/* Imagen */}
                        <div style={{ width: '100%', height: '100px', overflow: 'hidden', borderRadius: '8px', marginBottom: '0.4rem', cursor: 'zoom-in', background: 'rgba(0,0,0,0.3)', position: 'relative' }} onClick={() => setImagenAmpliada({ url: imagenUrl, nombre: p.nombre })}>
                        <img src={imagenUrl} alt={p.nombre} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                        {/* ✅ CAMBIADO: usar p.ref || p.sku para que coincida con el cache */}
                        {imagenesCache[p.ref || p.sku] === undefined && (
                            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                            <Loader2 size={16} className="spin-anim" style={{ color: '#fff' }} />
                            </div>
                        )}
                        </div>

                  <div className="product-header">
                    <span className="product-sku">{p.sku}</span>
                    <span className="product-marca">{p.marca}</span>
                  </div>
                  <div className="product-name">{p.nombre}</div>
                  <div className="product-footer">
                    <span className="product-price">
                      {sinPrecio ? <span style={{ color: '#f87171' }}>Sin precio</span> : formatUSD(precioUSD)}
                    </span>
                    <span className="product-unit">{p.uni_venta || 'UND'}</span>
                  </div>
                  {p.stock_bl !== null && (
                    <div className={`product-stock ${sinStock ? 'out' : ''}`}>
                      {sinStock ? '⚠️ Sin stock' : `Stock: ${p.stock_bl}`}
                    </div>
                  )}

                  {/* Controles */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                      <button onClick={(e) => { e.stopPropagation(); updateLocalCantidad(p.sku, cantidad - 1); }} disabled={cantidad <= 0 || sinPrecio} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff', cursor: 'pointer', padding: '2px 6px', borderRadius: '4px', opacity: cantidad <= 0 ? 0.3 : 1 }}>
                        <Minus size={10} />
                      </button>
                      <input type="number" min="0" value={cantidad} onChange={(e) => updateLocalCantidad(p.sku, e.target.value)} disabled={sinPrecio} onClick={(e) => e.stopPropagation()} style={{ width: '40px', background: 'rgba(255,255,255,0.05)', border: 'none', color: '#fff', fontSize: '0.65rem', textAlign: 'center', outline: 'none', borderRadius: '4px', padding: '2px 4px' }} />
                      <button onClick={(e) => { e.stopPropagation(); updateLocalCantidad(p.sku, cantidad + 1); }} disabled={sinPrecio} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff', cursor: 'pointer', padding: '2px 6px', borderRadius: '4px', opacity: sinPrecio ? 0.3 : 1 }}>
                        <Plus size={10} />
                      </button>
                    </div>

                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '2px', background: 'rgba(167,139,250,0.08)', borderRadius: '4px', padding: '2px 4px' }}>
                        <Percent size={8} style={{ color: '#a78bfa' }} />
                        <input type="number" min="0" max="100" step="0.01" value={descPropuesta} onChange={(e) => updateLocalDescuento(p.sku, e.target.value)} disabled={sinPrecio} onClick={(e) => e.stopPropagation()} style={{ width: '28px', background: 'transparent', border: 'none', color: '#fff', fontSize: '0.55rem', textAlign: 'center', outline: 'none' }} />
                        <span style={{ fontSize: '0.45rem', color: '#a78bfa' }}>%</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '2px', background: 'rgba(34,197,94,0.08)', borderRadius: '4px', padding: '2px 4px' }}>
                        <Percent size={8} style={{ color: '#4ade80' }} />
                        <input type="number" min="0" max="100" step="0.01" value={descPAC} onChange={(e) => updateLocalDescuentoPAC(p.sku, e.target.value)} disabled={sinPrecio} onClick={(e) => e.stopPropagation()} style={{ width: '28px', background: 'transparent', border: 'none', color: '#fff', fontSize: '0.55rem', textAlign: 'center', outline: 'none' }} />
                        <span style={{ fontSize: '0.45rem', color: '#4ade80' }}>%</span>
                      </div>
                    </div>

                    <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#fff', textAlign: 'center' }}>{formatUSD(subtotal)}</span>

                    {!added ? (
                      <button onClick={(e) => { e.stopPropagation(); handleAddProductLocal(p); }} disabled={sinPrecio || cantidad <= 0} className="new-order-btn" style={{ width: '100%', padding: '4px', fontSize: '0.6rem', minWidth: 'auto', opacity: (sinPrecio || cantidad <= 0) ? 0.4 : 1, background: (sinPrecio || cantidad <= 0) ? 'rgba(255,255,255,0.08)' : 'var(--text-theme, #00ba52)', cursor: (sinPrecio || cantidad <= 0) ? 'not-allowed' : 'pointer' }}>
                        <Plus size={10} /> Agregar
                      </button>
                    ) : (
                      <button onClick={(e) => { e.stopPropagation(); handleRemoveProduct(p.sku); }} style={{ width: '100%', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', cursor: 'pointer', padding: '4px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', fontSize: '0.6rem' }}>
                        <Trash2 size={10} /> Quitar
                      </button>
                    )}
                  </div>

                  {added && <div className="added-badge"><CheckCircle size={14} /> Agregado</div>}
                </div>
              );
            })}
          </div>
        )}

        {/* Lightbox */}
        {imagenAmpliada && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }} onClick={() => setImagenAmpliada(null)}>
            <img src={imagenAmpliada.url} alt={imagenAmpliada.nombre} style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' }} />
            <button style={{ position: 'absolute', top: '20px', right: '20px', background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', padding: '10px', borderRadius: '50%', cursor: 'pointer' }}>
              <XIcon size={24} />
            </button>
          </div>
        )}
      </div>

      {/* COLUMNA 3 - RESUMEN */}
      <VistaResumen hook={hook} />

      {successModal.show && (
  <>
    <div className="order-panel-overlay" style={{ zIndex: 1001 }} onClick={closeSuccessModal} />
    <div style={{ 
      position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', 
      background: 'rgba(15, 20, 35, 0.98)', backdropFilter: 'blur(20px)', 
      borderRadius: '20px', padding: '2rem', textAlign: 'center', zIndex: 1002, 
      boxShadow: '0 10px 30px rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.08)', 
      width: '90%', maxWidth: '550px', maxHeight: '80vh', overflow: 'auto', 
      fontFamily: 'Montserrat, sans-serif' 
    }}>
      <CheckCircle size={48} style={{ color: '#00ba52', marginBottom: '1rem' }} />
      <p style={{ color: '#fff', fontSize: '1rem', fontWeight: 600, margin: '0.5rem 0' }}>
        {successModal.message}
      </p>

      {/* ✅ SKUs no procesados */}
      {successModal.skus_no_procesados && successModal.skus_no_procesados.length > 0 && (
        <div style={{ 
          marginTop: '1rem', textAlign: 'left', 
          background: 'rgba(239,68,68,0.1)', 
          border: '1px solid rgba(239,68,68,0.3)', 
          borderRadius: '8px', padding: '0.8rem' 
        }}>
          <h4 style={{ color: '#f87171', fontSize: '0.8rem', marginBottom: '0.5rem', fontWeight: 600 }}>
            ⚠️ SKUs no procesados ({successModal.skus_no_procesados.length}):
          </h4>
          <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
            {successModal.skus_no_procesados.map((sku, idx) => (
              <div key={sku} style={{ 
                color: '#f87171', fontSize: '0.7rem', fontFamily: 'monospace', 
                padding: '2px 0', borderBottom: '1px solid rgba(239,68,68,0.1)' 
              }}>
                • {sku}
              </div>
            ))}
          </div>
          <p style={{ color: '#f87171', fontSize: '0.6rem', marginTop: '0.5rem', fontStyle: 'italic' }}>
            Estos SKUs no existen en la tabla de artículos de SQL Server.
          </p>
        </div>
      )}

      {/* ✅ Pedidos creados */}
      {successModal.pedidos && successModal.pedidos.length > 0 && (
        <div style={{ marginTop: '1rem', textAlign: 'left' }}>
          {successModal.pedidos.map((pedido, idx) => (
            <div key={idx} style={{ 
              background: 'rgba(255,255,255,0.05)', borderRadius: '8px', 
              padding: '0.5rem', marginBottom: '0.5rem', 
              display: 'flex', justifyContent: 'space-between', alignItems: 'center' 
            }}>
              <div>
                <div style={{ color: '#00ba52', fontWeight: 600 }}>
                  Pedido #{pedido.factNum}
                  {pedido.lote && <span style={{ fontSize: '0.7rem', color: '#888' }}> (Lote {pedido.lote})</span>}
                </div>
                <div style={{ color: '#aaa', fontSize: '0.8rem' }}>
                  Cliente: {pedido.clienteId} | {pedido.lineas} producto(s) | {pedido.moneda}
                </div>
              </div>
              <div style={{ color: '#fff', fontWeight: 700 }}>{formatUSD(pedido.total)}</div>
            </div>
          ))}
        </div>
      )}

      <button onClick={closeSuccessModal} style={{ 
        marginTop: '1.5rem', background: '#00ba52', color: '#000', 
        border: 'none', padding: '0.7rem 2rem', borderRadius: '10px', 
        fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', 
        fontFamily: 'Montserrat, sans-serif', boxShadow: '0 4px 10px rgba(0,186,82,0.3)' 
      }}>
        Aceptar
      </button>
    </div>
  </>
)}
    </div>
  );
};

export default VistaTarjetasImagen;